/**
 * Lógica pura de PLANIFICACIÓN SEMANAL. Sin React ni red.
 * Fechas de día: `AAAA-MM-DD`. Instantes: ISO-8601 UTC. Franjas del calendario
 * en UTC (determinista en tests y en el navegador).
 */
import type {
  CapacitySummary,
  InboxTask,
  Planning,
  PlanningConflict,
  PlanningKind,
  ProposedPlanning,
  WorkSchedule,
} from '../../stores/planningApi';

export const DURACION_PENDIENTE = 'DURACIÓN PENDIENTE';
export const HOUR_START = 7; // primera hora visible del calendario
export const HOUR_END = 21; // última hora visible
export const SLOT_MIN = 30; // granularidad de arrastre / redimensión

/** Lunes de la semana de `date` (AAAA-MM-DD, calendario UTC). */
export function weekStart(date: string): string {
  const d = new Date(`${date}T12:00:00Z`);
  const dow = d.getUTCDay(); // 0=domingo … 6=sábado
  const diff = dow === 0 ? -6 : 1 - dow;
  d.setUTCDate(d.getUTCDate() + diff);
  return d.toISOString().slice(0, 10);
}

export const addDays = (date: string, n: number): string => {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

/** Los 7 días (AAAA-MM-DD) de la semana que empieza en `from`. */
export const weekDays = (from: string): string[] => Array.from({ length: 7 }, (_, i) => addDays(from, i));

/** «martes, 6 de octubre». */
export const fmtLongDay = (date: string): string =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });

/** «mar 6 oct». */
export const fmtShortDay = (date: string): string =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });

/** HH:MM de un instante ISO UTC. */
export const hhmm = (iso: string): string => iso.slice(11, 16);

/** «09:30» → minutos desde medianoche. */
export const timeToMin = (time: string): number => {
  const [h, m] = time.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

/** Minutos desde medianoche → «09:30». */
export const minToTime = (min: number): string => {
  const m = Math.max(0, Math.min(24 * 60 - 1, Math.round(min)));
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};

/** Día e instante ISO UTC de un planificado. */
export const dayOf = (iso: string): string => iso.slice(0, 10);
export const minutesOf = (iso: string): number => timeToMin(hhmm(iso));

/** Construye `AAAA-MM-DDTHH:MM:00Z` (contrato UTC de /planning). */
export const isoAt = (date: string, time: string): string => `${date}T${minToTime(timeToMin(time))}:00Z`;

/** Fin = inicio + duración. */
export const isoEnd = (startsAt: string, durationMin: number): string => {
  const end = new Date(`${startsAt.replace(/Z$/, '')}Z`);
  end.setUTCMinutes(end.getUTCMinutes() + durationMin);
  return end.toISOString().slice(0, 19) + 'Z';
};

/** Duración visible: null → «DURACIÓN PENDIENTE» (nunca se inventa). */
export const durationLabel = (minutes: number | null | undefined): string =>
  minutes === null || minutes === undefined ? DURACION_PENDIENTE : `${minutes} min`;

/** 90 → «1h 30m»; 45 → «45m». */
export const fmtMinutes = (min: number): string => {
  const m = Math.round(Math.max(0, min));
  return m >= 60 ? `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m` : `${m}m`;
};

export const KIND_LABEL: Record<PlanningKind, string> = {
  TASK: 'Tarea',
  MEETING: 'Reunión',
  CALL: 'Llamada',
  BREAK: 'Descanso',
};

/** Colores por tipo de bloque (tokens theme-aware: válidos en claro y oscuro). */
export const KIND_CLS: Record<PlanningKind, string> = {
  TASK: 'border-primary/50 bg-primary/15 text-primary',
  MEETING: 'border-tertiary/50 bg-tertiary/15 text-tertiary',
  CALL: 'border-secondary/50 bg-secondary/15 text-secondary',
  BREAK: 'border-outline-variant bg-surface-container-highest text-on-surface-variant',
};

export const CONFLICT_LABEL: Record<string, string> = {
  TASK_TASK_OVERLAP: 'Se solapa con otra tarea',
  TASK_MEETING_OVERLAP: 'Se solapa con una reunión',
  TASK_CALL_OVERLAP: 'Se solapa con una llamada',
  OUTSIDE_WORK_SCHEDULE: 'Fuera del horario laboral',
  AFTER_DUE_DATE: 'Posterior a la fecha límite',
  OVER_CAPACITY: 'Supera la capacidad del día',
};

export const conflictLabel = (code: string): string => CONFLICT_LABEL[code] ?? code;

/** Índice planningId → códigos de conflicto. */
export function conflictsByPlanning(conflicts: PlanningConflict[]): Map<string, string[]> {
  const m = new Map<string, string[]>();
  for (const c of conflicts) {
    const list = m.get(c.planningId) ?? [];
    if (!list.includes(c.code)) list.push(c.code);
    m.set(c.planningId, list);
    if (c.otherPlanningId) {
      const other = m.get(c.otherPlanningId) ?? [];
      if (!other.includes(c.code)) other.push(c.code);
      m.set(c.otherPlanningId, other);
    }
  }
  return m;
}

/* ── Geometría del calendario ─────────────────────────────────────────── */

export interface BlockStyle {
  topPct: number;
  heightPct: number;
  leftPct: number;
  widthPct: number;
}

const GRID_MIN = (HOUR_END - HOUR_START) * 60;

/** Posición de un bloque dentro de su columna de día. */
export function blockStyle(minutesFromMidnight: number, durationMin: number, lane = 0, lanes = 1): BlockStyle {
  const start = Math.max(0, minutesFromMidnight - HOUR_START * 60);
  const dur = Math.max(SLOT_MIN, durationMin);
  return {
    topPct: (start / GRID_MIN) * 100,
    heightPct: Math.max(2, (dur / GRID_MIN) * 100),
    leftPct: lanes > 1 ? (lane / lanes) * 100 : 0,
    widthPct: lanes > 1 ? 100 / lanes : 100,
  };
}

/** Franja sombreada del horario laboral dentro de la columna de un día. */
export function workShadeStyle(start: string, end: string): BlockStyle {
  const s = Math.max(0, timeToMin(start) - HOUR_START * 60);
  const e = Math.min(GRID_MIN, timeToMin(end) - HOUR_START * 60);
  return { topPct: (s / GRID_MIN) * 100, heightPct: Math.max(0, ((e - s) / GRID_MIN) * 100), leftPct: 0, widthPct: 100 };
}

/** Horas visibles del calendario («07:00» … «21:00»). */
export const hourLabels: string[] = Array.from({ length: HOUR_END - HOUR_START + 1 }, (_, i) => minToTime((HOUR_START + i) * 60));

/** Valida una duración de planificación: entero de 15 a 480 minutos. */
export const isValidDuration = (min: number): boolean => Number.isInteger(min) && min >= 15 && min <= 480;

/** Ajusta una duración a la granularidad de 30 min dentro de [15, 480]. */
export function snapDuration(min: number): number {
  const snapped = Math.round(min / SLOT_MIN) * SLOT_MIN;
  return Math.min(480, Math.max(SLOT_MIN, snapped || SLOT_MIN));
}

/**
 * Nueva franja al mover un planificado a `date` + `time`, recortada para no
 * salirse del día visible.
 */
export function movedRange(date: string, time: string, durationMin: number): { startsAt: string; endsAt: string; durationMin: number } {
  const start = Math.min(Math.max(timeToMin(time), HOUR_START * 60), HOUR_END * 60 - SLOT_MIN);
  const duration = snapDuration(Math.min(durationMin, HOUR_END * 60 - start));
  const startsAt = isoAt(date, minToTime(start));
  return { startsAt, endsAt: isoEnd(startsAt, duration), durationMin: duration };
}

/* ── Capacidad ────────────────────────────────────────────────────────── */

export interface CapacityView {
  workLabel: string;
  meetingsLabel: string;
  tasksLabel: string;
  availableLabel: string;
  usedPct: number;
  availablePct: number;
  negative: boolean;
}

/** Resumen visual de capacidad: HORAS LABORALES − REUNIONES/LLAMADAS − TAREAS = DISPONIBLE. */
export function capacityView(c: CapacitySummary): CapacityView {
  const meetingTotal = c.meetingMinutes + c.callMinutes;
  const available = c.workMinutes - meetingTotal - c.taskMinutes;
  const base = Math.max(1, c.workMinutes);
  return {
    workLabel: fmtMinutes(c.workMinutes),
    meetingsLabel: fmtMinutes(meetingTotal),
    tasksLabel: fmtMinutes(c.taskMinutes),
    availableLabel: fmtMinutes(Math.abs(available)),
    usedPct: Math.min(100, ((meetingTotal + c.taskMinutes) / base) * 100),
    availablePct: Math.max(0, (available / base) * 100),
    negative: available < 0,
  };
}

/** Horas laborales previstas de la semana (minutos) según el horario declarado. */
export function scheduledWorkMinutes(schedule: WorkSchedule | null, days: string[]): number {
  if (!schedule) return 0;
  return days.reduce((sum, date) => {
    const d = schedule.days.find((x) => x.date === date);
    if (!d || !d.isWorkday || !d.start || !d.end) return sum;
    return sum + Math.max(0, timeToMin(d.end) - timeToMin(d.start));
  }, 0);
}

/* ── Inbox ────────────────────────────────────────────────────────────── */

export interface InboxRowView {
  id: string;
  taskCode: string;
  title: string;
  projectName: string;
  process: string;
  priority: string;
  dueLabel: string;
  durationLabel: string;
  estimatedMinutes: number | null;
}

export const PROCESS_LABEL = (p: string | null): string => (p && p !== 'PENDING' ? p : 'Sin clasificar');

export function inboxRow(t: InboxTask): InboxRowView {
  return {
    id: t.id,
    taskCode: t.taskCode,
    title: t.title,
    projectName: t.project?.name ?? '—',
    process: PROCESS_LABEL(t.process),
    priority: t.priority,
    dueLabel: t.dueDate ? `Vence ${t.dueDate}` : 'Sin fecha',
    durationLabel: durationLabel(t.estimatedMinutes),
    estimatedMinutes: t.estimatedMinutes,
  };
}

/* ── Propuesta de Nexus (determinista, solo propuesta) ───────────────── */

const PRIORITY_RANK: Record<string, number> = { URGENT: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };

/** Orden de propuesta: vencidas, alta prioridad, próxima a vencer, resto. */
export function proposalOrder(tasks: InboxTask[]): InboxTask[] {
  return [...tasks].sort((a, b) => {
    const pa = PRIORITY_RANK[a.priority] ?? 4;
    const pb = PRIORITY_RANK[b.priority] ?? 4;
    if (pa !== pb) return pa - pb;
    const da = a.dueDate ?? '9999-12-31';
    const db = b.dueDate ?? '9999-12-31';
    return da < db ? -1 : da > db ? 1 : 0;
  });
}

/**
 * Propone franjas para las tareas del inbox dentro del horario laboral de la
 * semana. Las tareas sin estimación se listan como «DURACIÓN PENDIENTE» sin
 * franja propuesta (nunca se inventa una duración). Nada se guarda: la persona
 * revisa, modifica y confirma.
 */
export function buildWeekProposal(
  tasks: InboxTask[],
  from: string,
  schedule: WorkSchedule | null,
  alreadyPlanned: Planning[] = [],
  now: { date: string; time: string } = { date: from, time: '09:00' },
): { proposals: ProposedPlanning[]; notes: string[] } {
  const days = weekDays(from);
  const busy = new Map<string, { start: number; end: number }[]>();
  for (const date of days) busy.set(date, []);
  for (const p of alreadyPlanned) {
    const date = dayOf(p.startsAt);
    const list = busy.get(date);
    if (list) list.push({ start: minutesOf(p.startsAt), end: minutesOf(p.endsAt) });
  }

  const notes: string[] = [];
  const proposals: ProposedPlanning[] = [];
  let cursor = { date: days.find((d) => d >= now.date) ?? days[0], min: timeToMin(now.time) };

  for (const t of proposalOrder(tasks)) {
    const base: ProposedPlanning = {
      key: `proposal-${t.id}`,
      taskId: t.id,
      taskCode: t.taskCode,
      title: t.title,
      project: t.project ?? null,
      process: t.process,
      priority: t.priority,
      dueDate: t.dueDate,
      estimatedMinutes: t.estimatedMinutes,
      suggested: null,
      reason: '',
    };

    if (t.estimatedMinutes === null) {
      proposals.push({ ...base, reason: 'Sin estimación de duración: revisa y decide cuánto dedicarle.' });
      notes.push(`${t.taskCode} · ${DURACION_PENDIENTE}`);
      continue;
    }

    const duration = snapDuration(t.estimatedMinutes);
    let placed: { date: string; time: string; durationMin: number } | null = null;

    for (const date of days.filter((d) => d >= cursor.date)) {
      const day = schedule?.days.find((x) => x.date === date);
      const isWorkday = !day || day.isWorkday;
      const dayStart = timeToMin(day?.start ?? schedule?.workStart ?? '09:00');
      const dayEnd = timeToMin(day?.end ?? schedule?.workEnd ?? '18:00');
      if (!isWorkday) continue;

      let min = date === cursor.date ? Math.max(cursor.min, dayStart) : dayStart;
      const list = busy.get(date) ?? [];
      while (min + duration <= dayEnd) {
        const clash = list.some((b) => min < b.end && min + duration > b.start);
        if (!clash) {
          placed = { date, time: minToTime(min), durationMin: duration };
          list.push({ start: min, end: min + duration });
          cursor = { date, min: min + duration };
          break;
        }
        min += SLOT_MIN;
      }
      if (placed) break;
    }

    proposals.push(
      placed
        ? { ...base, suggested: placed, reason: `Primera franja libre según prioridad (${t.priority.toLowerCase()}).` }
        : { ...base, reason: 'Sin hueco disponible en la semana: revisa la duración o mueve otra planificación.' },
    );
    if (!placed) notes.push(`${t.taskCode} · sin hueco en la semana`);
  }

  return { proposals, notes: notes.length ? notes : ['Todas las tareas tienen hueco propuesto.'] };
}

/** Filas de agenda móvil para un día (ordenadas por hora). */
export function agendaRows(items: Planning[], date: string): Planning[] {
  return items
    .filter((p) => dayOf(p.startsAt) === date && p.status !== 'CANCELLED')
    .sort((a, b) => (a.startsAt < b.startsAt ? -1 : a.startsAt > b.startsAt ? 1 : 0));
}

/** Texto accesible de un bloque. */
export function blockAria(p: Planning): string {
  const kind = KIND_LABEL[p.kind];
  const dur = durationLabel(p.durationMin);
  return `${kind} ${p.title}, ${hhmm(p.startsAt)} a ${hhmm(p.endsAt)}, ${dur}`;
}
